/**
 * LIB lane — "is this MBMR process library-linked, in sync or drifted, and what differs".
 *
 * SELF-CONTAINED ON PURPOSE. The MBMR editor's process view
 * (src/pages/Batch-Processing/mbmr/ProcessHierarchy.js and
 * src/pages/Batch-Processing/MBMRConfig/*) is owned by another lane right now, so this
 * ships as a drop-in component instead of an edit to those files.
 *
 * MOUNTING (for the integrator)
 *   import ProcessLineageBadge from '@/components/library/ProcessLineageBadge';
 *
 *   // in the process header row of ProcessHierarchy.js, beside the process name/code:
 *   <ProcessLineageBadge processId={process.process_id} />
 *
 *   // in the MBMRConfig process panel header, where there is more room:
 *   <ProcessLineageBadge processId={processId} showSummary />
 *
 * It fetches its own data (one GET per process), renders nothing at all while loading
 * and nothing when the process has no library origin, so it is safe to drop into a
 * list without disturbing the layout. Pass `lineage` to render from data the parent
 * already has and skip the fetch.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Drawer, Space, Tag, Tooltip, Typography } from 'antd';
import { Library, Sparkles } from 'lucide-react';
import ProcessDiffView from './ProcessDiffView';
import { fetchProcessLineage, verdictMeta } from './lineageApi';

const { Text, Paragraph } = Typography;

const ProcessLineageBadge = ({ processId, lineage: lineageProp, showSummary, showWhenUnlinked }) => {
  const [lineage, setLineage] = useState(lineageProp || null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (lineageProp) {
      setLineage(lineageProp);
      return;
    }
    if (!processId) return;
    let alive = true;
    fetchProcessLineage(processId, (data) => {
      if (alive) setLineage(data);
    });
    return () => {
      alive = false;
    };
  }, [processId, lineageProp]);

  if (!lineage) return null;

  if (!lineage.linked) {
    if (!showWhenUnlinked) return null;
    return (
      <Tooltip title="This process was not copied from a Process Library block, so there is nothing to compare it against.">
        <Tag>Not library-linked</Tag>
      </Tooltip>
    );
  }

  const meta = verdictMeta(lineage.verdict);
  const inferred = String(lineage.link_origin || '').startsWith('BACKFILL');

  const tooltip = [
    `Library: ${meta.label}`,
    `From library block ${lineage.library_process_code || ''} “${lineage.library_process_name}”`,
    `Copied from version ${lineage.source_version}${
      lineage.library_version_now !== lineage.source_version ? ` (block is now ${lineage.library_version_now})` : ''
    }`,
    lineage.summary,
    inferred
      ? 'This link was inferred from the operations after the fact — nothing was recorded when the copy was taken, so recipe drift cannot be told apart from a library edit.'
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Space size={4} wrap>
        <Tooltip title={tooltip}>
          <Tag
            color={meta.color}
            style={{ cursor: 'pointer', margin: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            {/* An icon, not a phrase: on a 360px rail "Library: Drifted · inferred" was
                wider than the process name it sat beside. The verdict is the colour; the
                words are one hover away, and the drawer one click. */}
            <Library size={12} strokeWidth={2} style={{ verticalAlign: -2 }} aria-label={`Library: ${meta.label}`} />
          </Tag>
        </Tooltip>
        {inferred && (
          <Tooltip title="Inferred: lineage read from operation evidence, not recorded at copy time.">
            <Tag color="purple" style={{ margin: 0 }} aria-label="inferred">
              <Sparkles size={12} strokeWidth={2} style={{ verticalAlign: -2 }} />
            </Tag>
          </Tooltip>
        )}
        {showSummary && lineage.summary ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {lineage.summary}
          </Text>
        ) : null}
      </Space>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        width={760}
        destroyOnHidden
        title={
          <Space direction="vertical" size={0}>
            <Text strong>{lineage.process_name}</Text>
            <Text type="secondary">
              vs library block {lineage.library_process_code} “{lineage.library_process_name}”
            </Text>
          </Space>
        }
      >
        <Space size={6} wrap style={{ marginBottom: 12 }}>
          <Tag color={meta.color}>{lineage.verdict_label}</Tag>
          <Tag>copied from v{lineage.source_version}</Tag>
          <Tag>block now v{lineage.library_version_now}</Tag>
          <Tag>{lineage.library_status}</Tag>
          {lineage.copied_at ? <Tag>copied {new Date(lineage.copied_at).toLocaleDateString()}</Tag> : null}
          {inferred ? <Tag color="purple">{lineage.link_origin_label}</Tag> : null}
        </Space>

        {lineage.library_changed_since_copy === null && (
          <Paragraph type="secondary">
            Nothing was recorded when this copy was taken, so the system cannot tell whether the recipe
            was edited or the library block moved. What it can say is where they differ today.
          </Paragraph>
        )}
        {lineage.summary ? <Paragraph type="secondary">{lineage.summary}</Paragraph> : null}

        <ProcessDiffView diff={lineage.diff} notCompared={lineage.not_compared} />
      </Drawer>
    </>
  );
};

ProcessLineageBadge.propTypes = {
  processId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Pre-fetched payload from /library/lineage/process/ — skips the fetch. */
  lineage: PropTypes.object,
  /** Also print the one-line "what differs" summary next to the tag. */
  showSummary: PropTypes.bool,
  /** Render a "Not library-linked" tag instead of nothing when there is no origin. */
  showWhenUnlinked: PropTypes.bool,
};

export default ProcessLineageBadge;
